import React, { useState, useEffect, useCallback } from 'react'
import { FaTrash } from 'react-icons/fa'
import {
  Button,
  Stack,
  Text,
  Box,
  FormControl,
  DateChooser,
  Icon,
  ConfirmButton,
  SimpleModal,
  MentionInput,
  MentionText
} from '@/components/lib'
import UserChip from '@/components/includes/UserChip'
import api from '@/api'
import utils from '@/utils/utils'
import useStore from '@/store'

export default function ParticipantNoteEditor ({
  participant,
  note,
  /** For new notes: pre-fill “Shown on calendar” (e.g. day clicked in the timeline calendar). */
  defaultInRelationToDate,
  isOpen,
  onClose,
  onSaved
}) {
  const { user, trial, getTrialUsers, deleteParticipantNote } = useStore()
  // Only people who can read this trial's notes: the API drops a mention of
  // anyone who can't, silently, so offering someone it would drop would show a
  // name in the saved note that notified nobody. The API says who those are.
  const mentionableUsers = getTrialUsers(trial?._id)
    .filter(u => utils.isMentionableIn(u, 'participantNotes'))
  const [text, setText] = useState('')
  const [relationDate, setRelationDate] = useState(() => new Date())
  const [saving, setSaving] = useState(false)

  const isEdit = !!(note && note._id)
  const canCreate = utils.canWriteParticipantNotes(user, trial)
  const canEditExisting =
    isEdit &&
    (utils.canManageTrial(user, trial) ||
      (user?._id != null &&
        note?.user != null &&
        String(user._id) === String(note.user)))
  const canEdit = isEdit ? canEditExisting : canCreate

  useEffect(() => {
    if (!isOpen) return
    if (isEdit) {
      setText(note.note ?? '')
      const raw = note.inRelationToDate ?? note.createdAt
      setRelationDate(raw ? new Date(raw) : new Date())
    } else {
      setText('')
      if (defaultInRelationToDate != null) {
        const d =
          defaultInRelationToDate instanceof Date
            ? new Date(defaultInRelationToDate.getTime())
            : new Date(defaultInRelationToDate)
        setRelationDate(!Number.isNaN(d.getTime()) ? d : new Date())
      } else {
        setRelationDate(new Date())
      }
    }
  }, [isOpen, isEdit, note, defaultInRelationToDate])

  const handleClose = useCallback(() => {
    onClose?.()
  }, [onClose])

  const handleDelete = useCallback(() => {
    if (!participant?.trial || !participant?._id || !note?._id) return
    api.participantNotes.delete(
      participant.trial,
      participant._id,
      note._id,
      () => {
        deleteParticipantNote(note._id)
        utils.toast('success', 'Note deleted')
        handleClose()
      },
      (err) => utils.toast('error', err.message || 'Could not delete note')
    )
  }, [participant, note, deleteParticipantNote, handleClose])

  const handleSave = useCallback(() => {
    if (!participant?.trial || !participant?._id || !canEdit) return
    setSaving(true)
    const relIso =
      relationDate instanceof Date && !Number.isNaN(relationDate.getTime())
        ? relationDate.toISOString()
        : new Date().toISOString()
    const payload = { note: text, inRelationToDate: relIso }
    if (isEdit) {
      api.participantNotes.update(
        participant.trial,
        participant._id,
        note._id,
        payload,
        (saved) => {
          setSaving(false)
          utils.toast('success', 'Note saved')
          onSaved?.(saved)
          handleClose()
        },
        (err) => {
          setSaving(false)
          utils.toast('error', err.message || 'Could not save note')
        }
      )
    } else {
      api.participantNotes.create(
        participant.trial,
        participant._id,
        payload,
        (saved) => {
          setSaving(false)
          utils.toast('success', 'Note created')
          onSaved?.(saved)
          handleClose()
        },
        (err) => {
          setSaving(false)
          utils.toast('error', err.message || 'Could not create note')
        }
      )
    }
  }, [participant, isEdit, note, text, relationDate, onSaved, handleClose, canEdit])

  const authorForChip = isEdit && note?.userObject
    ? { ...note.userObject, _id: note.user }
    : user

  return (
    <SimpleModal
      size='md'
      isOpen={isOpen}
      onClose={handleClose}
      header={isEdit ? (canEdit ? 'Edit note' : 'Note') : 'New note'}
      body={
        <Stack gap={4}>
          <Stack
            direction='row'
            alignItems='center'
            gap={4}
            flexWrap='wrap'
          >
            {authorForChip && (
              <Box>
                <Text fontSize='xs' color='fg.muted' mb={1}>Author</Text>
                <UserChip user={authorForChip} compact />
              </Box>
            )}
            {isEdit && note?.createdAt && (
              <Box>
                <Text fontSize='xs' color='fg.muted' mb={1}>Created</Text>
                <Text fontSize='sm'>{utils.formatDateTime(note.createdAt)}</Text>
              </Box>
            )}
            {isEdit && (
              <Box>
                <Text fontSize='xs' color='fg.muted' mb={1}>Last updated</Text>
                <Text fontSize='sm'>
                  {note.updatedAt ? utils.formatDateTime(note.updatedAt) : '—'}
                </Text>
              </Box>
            )}
            {!isEdit && (
              <Text fontSize='sm' color='fg.muted'>
                Saving will record you as the author of this note.
              </Text>
            )}
          </Stack>

          <Box>
            <Text fontSize='xs' color='fg.muted' mb={1}>Note</Text>
            {canEdit
              ? (
                <MentionInput
                  autoFocus
                  users={mentionableUsers}
                  value={text}
                  onChange={setText}
                  placeholder='Write a note. Type @ to mention someone…'
                  minH='160px'
                  rows={6}
                />)
              : (
                <MentionText
                  text={text}
                  currentUserId={user?._id}
                  minH='160px'
                  fontSize='sm'
                />)}
          </Box>
          {canEdit
            ? (
              <FormControl
                label='Date the note is in relation to'
                helperText='Defaults to the day the note was created.'
              >
                <DateChooser
                  value={relationDate}
                  onChange={(d) => setRelationDate(d || new Date())}
                />
              </FormControl>
              )
            : (
              <Box>
                <Text fontSize='xs' color='fg.muted' mb={1}>Shown on calendar</Text>
                <Text fontSize='sm'>
                  {relationDate instanceof Date && !Number.isNaN(relationDate.getTime())
                    ? utils.formatDateTime(relationDate.toISOString())
                    : '—'}
                </Text>
              </Box>
              )}
        </Stack>
      }
      footer={
        <Stack direction='row' justifyContent='space-between' alignItems='center' w='100%' flexWrap='wrap' gap={2}>
          <Box>
            {canEdit && isEdit && (
              <ConfirmButton
                trigger={
                  <Button variant='ghost' colorPalette='red' size='sm' disabled={saving}>
                    <Icon as={FaTrash} />
                  </Button>
                }
                header='Really delete this note?'
                onConfirm={handleDelete}
                actionColor='red'
              />
            )}
          </Box>
          <Stack direction='row' gap={2}>
            {canEdit
              ? (
                <>
                  <Button variant='ghost' onClick={handleClose} disabled={saving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} loading={saving}>
                    Save
                  </Button>
                </>
                )
              : (
                <Button onClick={handleClose}>Close</Button>
                )}
          </Stack>
        </Stack>
      }
    />
  )
}
